// @flow

import React from 'react';
import {makeStyles} from '@material-ui/styles';
import {
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Hidden,
  Typography,
} from '@material-ui/core';

import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';
import {colors} from '../../../../../assets/styles/colors';
import {useIsFeatureInTier} from '../../../../../utils/component';
import PremiumFeatureBadge from '../../../../../elements/PremiumFeatureBadge/PremiumFeatureBadge';

import {useFormContext} from 'react-hook-form';
import type {Theme} from '@material-ui/core';
import {useTypedSelector} from '../../../../../store';
import {useProductFormContext} from '../../../context/ProductForm';
import {useDebounceInput} from '../../../../on-boarding/utils';

const useStyles = makeStyles((theme: Theme) => ({
  contentSection: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    padding: theme.spacing(0, 3, 3, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 2, 2, 2),
    },
    background: 'white',
  },
  sectionTitle: {
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(2),
    },
  },
  sectionDescription: {
    color: colors.grey700,
    fontSize: '0.875rem',
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  sectionCheckbox: {
    '& .MuiFormControlLabel-label': {
      color: colors.grey700,
      fontSize: '0.875rem',
    },
  },
  infoText: {
    color: colors.grey900,
    fontSize: 14,
  },
  wrapSeoExample: {
    pointerEvents: 'none',
    background: '#FFFFFF',
    border: '1px solid #E8E8ED',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    borderRadius: 8,
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(1),
  },
  colorBlue: {
    color: '#2E24A3',
    fontSize: 14,
    fontWeight: 500,
  },
  colorGreen: {
    fontSize: 12,
    fontWeight: 500,
    color: '#005B24',
  },
  colorGray: {
    fontSize: 12,
    fontWeight: 500,
    color: '#717190',
  },
  disableSection: {
    pointerEvents: 'none',
    opacity: 0.5,
  },
}));

type Props = {
  disabled: boolean,
};

export default function Seo(props: Props) {
  const {disabled} = props;

  const hasProductMetadataFeature = useIsFeatureInTier('PRODUCT_SEO');
  const metadata = useTypedSelector(state => state.products.productMetadata);

  const {register, errors, control} = useFormContext();
  const {enabledSeo, setEnabledSeo} = useProductFormContext();

  const debouncedMetatitle = useDebounceInput(control, 'metaTitle', '', 300);
  const debouncedMetaDescription = useDebounceInput(
    control,
    'metaDescription',
    '',
    300
  );

  const classes = useStyles();

  const handleEnabledSeo = () => {
    setEnabledSeo(ps => !ps);
  };

  return (
    <section className={classes.contentSection}>
      <Typography className={classes.sectionTitle}>
        SEO de Producto {!hasProductMetadataFeature && <PremiumFeatureBadge />}
      </Typography>
      <div>
        <FormControlLabel
          control={
            <Checkbox
              disabled={!hasProductMetadataFeature}
              checked={enabledSeo}
              onChange={handleEnabledSeo}
              name="checkedI"
              color="primary"
            />
          }
          className={classes.sectionCheckbox}
          label="Generar metadatos para optimizar los motores de búsqueda."
        />

        {enabledSeo && (
          <>
            <Divider className={classes.divider} />
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <div className={classes.wrapSeoExample}>
                  <Typography className={classes.colorBlue}>
                    {debouncedMetatitle
                      ? debouncedMetatitle
                      : metadata
                      ? metadata.title
                      : '--'}
                  </Typography>
                  <Typography className={classes.colorGray}>
                    <Hidden>
                      {debouncedMetaDescription
                        ? debouncedMetaDescription
                        : metadata
                        ? metadata.description
                        : '--'}
                    </Hidden>
                  </Typography>
                </div>
              </Grid>
              <Grid item xs={12}>
                <CustomTextField
                  maxLength={5}
                  label="Título"
                  name="metaTitle"
                  autoComplete="metaTitle"
                  error={!!errors.metaTitle}
                  defaultValue={metadata ? metadata.title : ''}
                  helperText={errors.metaTitle ? errors.metaTitle.message : ''}
                  //   InputLabelProps={{shrink: !!(metadata && metadata.title)}}
                  type="text"
                  disabled={!hasProductMetadataFeature || disabled}
                  className={classes.productInputField}
                  inputRef={register({
                    required: 'Campo obligatorio',
                  })}
                />
              </Grid>
              <Grid item xs={12}>
                <CustomTextField
                  multiline
                  label="Descripción"
                  name="metaDescription"
                  autoComplete="metaDescription"
                  error={!!errors.metaDescription}
                  helperText={
                    errors.metaDescription ? errors.metaDescription.message : ''
                  }
                  defaultValue={metadata ? metadata.description : ''}
                  //   InputLabelProps={{
                  //     shrink: !!(metadata && metadata.description),
                  //   }}
                  type="text"
                  inputRef={register({required: 'Campo obligatorio'})}
                  rows={4}
                  disabled={!hasProductMetadataFeature || disabled}
                  className={classes.productInputField}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography className={classes.infoText}>
                  Se recomienda utilizar menos de 80 caracteres para el título y
                  menos de 160 caracteres para la descripción.
                </Typography>
              </Grid>
            </Grid>
          </>
        )}
      </div>
    </section>
  );
}
